import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as requests from '../api/requests';
import { UpgradeRequestEnum } from '../utils/enums';
import { addNotification, addErrorNotification } from './notificationsSlice';

const initialState = {
  accountsInfo: [],
  eventsInfo: [],
  blockchainKillSwitch: null,
  homePageInfo: [],
  imageNames: [],
  programs: [],
  purchaseHistory: [],
  pendingUpgradeRequests: 0,
};

// ------------------ ASYNC THUNKS ------------------

export const getAccountsInfo = createAsyncThunk(
  'adminHome/getAccountsInfo',
  async (query, thunkAPI) => {
    const res = await requests.getAdminHomeAccountsInfo(query);
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg = body.errmsg || 'Something went wrong while fetching accounts info!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const updateAccountInfo = createAsyncThunk(
  'adminHome/updateAccountInfo',
  async (accountInfo, thunkAPI) => {
    const res = await requests.updateAccount(accountInfo);
    const { ok } = res;
    const body = !ok && (await res.json());

    if (ok) {
      thunkAPI.dispatch(getAccountsInfo());
      thunkAPI.dispatch(addNotification('Account updated successfully!'));
    } else {
      const errmsg = body.errmsg || 'Something went wrong when updating account!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const upgradeAccount = createAsyncThunk(
  'adminHome/upgradeAccount',
  async (args, thunkAPI) => {
    const { username, action, accountType, entityName, sector, sectorName } = args;
    const res = await requests.upgradeAccount(
      username,
      action,
      accountType,
      entityName,
      sector,
      sectorName
    );
    const { ok } = res;
    const body = !ok && (await res.json());
    if (ok) {
      thunkAPI.dispatch(getAccountsInfo());
      thunkAPI.dispatch(addNotification('Account upgraded successfully!'));
    } else {
      const errmsg = body.errmsg || 'Something went wrong when upgrading account!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const getEventsInfo = createAsyncThunk(
  'adminHome/getEventsInfo',
  async (_, thunkAPI) => {
    const res = await requests.getAdminHomeEventsInfo();
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg = body.errmsg || 'Something went wrong while fetching events info!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const getHomePageInfo = createAsyncThunk(
  'adminHome/getHomePageInfo',
  async (_, thunkAPI) => {
    const res = await requests.getHomePageInfo();
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg = body.errmsg || 'Something went wrong while fetching home page stats!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const getPartnersImageName = createAsyncThunk(
  'adminHome/getPartnersImageName',
  async (_, thunkAPI) => {
    const res = await requests.getHomePagePartnersImageName();
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg = body.errmsg || 'Something went wrong when fetching partner image!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }
    return { ok, body };
  }
);

export const getPrograms = createAsyncThunk(
  'adminHome/getPrograms',
  async (_, thunkAPI) => {
    const res = await requests.getAllPrograms(true);
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg = body.errmsg || 'Something went wrong while fetching programs info!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

export const getPurchaseHistory = createAsyncThunk(
  'adminHome/getPurchaseHistory',
  async (_, thunkAPI) => {
    const res = await requests.getPurchaseHistory();
    const { ok } = res;
    const body = await res.json();

    if (!ok) {
      const errmsg =
        body.errmsg || 'Something went wrong while fetching the purchase history!';
      thunkAPI.dispatch(addErrorNotification(errmsg));
    }

    return { ok, body };
  }
);

// ------------------ SLICE ------------------

export const adminHomeSlice = createSlice({
  name: 'adminHome',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAccountsInfo.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          body.accountsInfo.forEach((acc) => {
            acc.senderEmail = acc.senderEmail || '';
            acc.senderName = acc.senderName || '';
          });
          const accountsInfo = body.accountsInfo;
          const pendingUpgradeRequests = accountsInfo.filter(
            (acc) => acc.upgradeRequest === UpgradeRequestEnum.PROCESSING
          ).length;
          state.accountsInfo = accountsInfo;
          state.pendingUpgradeRequests = pendingUpgradeRequests;
        }
      })

      .addCase(getEventsInfo.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          state.eventsInfo = body.eventsInfo;
          state.blockchainKillSwitch = body.blockchainKillSwitch || null;
        }
      })

      .addCase(getHomePageInfo.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          const fields = Object.keys(body).sort();
          state.homePageInfo = fields.map((key, i) => ({
            _id: i,
            field: key,
            value: body[key],
          }));
        }
      })

      .addCase(getPartnersImageName.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          state.imageNames = body.images.map((i) => ({ name: i }));
        }
      })

      .addCase(getPrograms.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          body.programs.forEach(
            (program) => (program.programDate = new Date(program.programDate).toString())
          );
          state.programs = body.programs;
        }
      })

      .addCase(getPurchaseHistory.fulfilled, (state, { payload }) => {
        const { ok, body } = payload;
        if (ok) {
          body.purchaseHistory.forEach((history) => {
            history.timestamp = new Date(history.timestamp).toString();
            const amountPaid = String(history.amountPaid);
            const len = amountPaid.length;
            history.amountPaid = `$${amountPaid.substring(0, len - 2)}.${amountPaid.substring(len - 2)}`;
          });
          state.purchaseHistory = body.purchaseHistory;
        }
      });
  },
});

export default adminHomeSlice.reducer;
